import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Home, Users, Wallet, HardHat, GraduationCap, Store, Handshake,
  BadgeCheck, Shield, Sparkles, MapPin, Building2, Landmark, Quote,
} from "lucide-react";
import ImpactCalculator from "@/components/ImpactCalculator";
import RiskWarning from "@/components/RiskWarning";

const money = (v, cur = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: (cur || "GBP").toUpperCase(), maximumFractionDigits: 0 }).format(v || 0);

const PHASES = [
  {
    key: "phase-1",
    name: "Phase 1",
    when: "2–3 years",
    scope: "Zimbabwe · Zambia · South Africa",
    jobs_low: 610,
    jobs_high: 980,
    who: "Bricklayers, plumbers, electricians, drivers, admin, suppliers",
  },
  {
    key: "phase-2",
    name: "Phase 2",
    when: "5 years",
    scope: "All 5 Southern African nations",
    jobs_low: 4300,
    jobs_high: 9250,
    who: "Trades, logistics, retail, agents, maintenance, management",
  },
  {
    key: "phase-3",
    name: "Phase 3",
    when: "10 years",
    scope: "Regional scale + community hubs",
    jobs_low: 12000,
    jobs_high: 25000,
    who: "Permanent roles, local businesses, skilled professionals",
  },
];

const FAIR_JOBS = [
  { icon: <BadgeCheck size={16}/>, text: "60% of entry roles reserved for youth & women" },
  { icon: <MapPin      size={16}/>, text: "80% of jobs go to people living locally" },
  { icon: <GraduationCap size={16}/>, text: "Free certified training via Pula Learn for every worker" },
  { icon: <Handshake   size={16}/>, text: "200–500 local agent roles across all countries" },
  { icon: <Shield      size={16}/>, text: "Fair living wage — no unpaid trials or exploitation" },
];

const FAIR_JOBS_DIFFERENT = [
  "All roles pay at least local living wage — no exploitation",
  "Every worker gets free safety gear + certified training",
  "Permanent contracts for maintenance, admin, and agent roles",
  "Skills stay in the community — workers build homes for their own families too",
];

const COMMUNITY_BENEFITS = [
  { icon: <Building2 size={18}/>, text: "New roads, water and electricity for whole areas" },
  { icon: <Store     size={18}/>, text: "Spaces for markets, schools and community halls" },
  { icon: <Handshake size={18}/>, text: "Local small businesses get priority contracts" },
  { icon: <Landmark  size={18}/>, text: "More land available for community gardens and farms" },
];

export default function ImpactPage() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/impact-reports");
        setReports(data.reports || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const totals = reports.reduce((a, r) => ({
    homes: a.homes + (r.homes_delivered || 0),
    families: a.families + (r.families_helped || 0),
    fees: a.fees + (r.fees_saved || 0),
  }), { homes: 0, families: 0, fees: 0 });

  return (
    <main data-testid="impact-page">
      {/* HERO */}
      <section className="mv-green-bg text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{ background: "radial-gradient(600px 300px at 20% 0%, var(--mv-gold), transparent 60%)" }}/>
        <div className="max-w-5xl mx-auto px-4 md:px-8 relative">
          <div className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "var(--mv-gold)" }}>§ Impact Summary</div>
          <h1 className="font-display text-4xl md:text-6xl mt-4 leading-[0.98]">
            Building homes, <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>creating livelihoods.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-white/85 text-[15.5px] leading-relaxed font-accent italic">
            "We don't just grow property value — we grow people, skills, and local economies that stay forever."
          </p>
        </div>
      </section>

      {/* PHASE TABLE — realistic job creation */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Realistic job creation — by phase</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          What we plan to build. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Publicly.</span>
        </h2>
        <div className="mt-8 overflow-x-auto" data-testid="impact-phase-table">
          <table className="w-full text-[14px]">
            <thead>
              <tr className="text-left border-b mv-hairline" style={{ color: "var(--mv-muted)" }}>
                <th className="py-3 pr-4 text-[11.5px] uppercase tracking-[0.18em]">Stage</th>
                <th className="py-3 pr-4 text-[11.5px] uppercase tracking-[0.18em]">Timeframe / scope</th>
                <th className="py-3 pr-4 text-[11.5px] uppercase tracking-[0.18em]">Total jobs</th>
                <th className="py-3 text-[11.5px] uppercase tracking-[0.18em]">Who benefits</th>
              </tr>
            </thead>
            <tbody>
              {PHASES.map((p) => (
                <tr key={p.key} className="border-b mv-hairline align-top" data-testid={`impact-${p.key}`}>
                  <td className="py-4 pr-4">
                    <div className="font-display text-lg mv-green-text">{p.name}</div>
                    <div className="text-[11.5px]" style={{ color: "var(--mv-muted)" }}>{p.when}</div>
                  </td>
                  <td className="py-4 pr-4 text-[13.5px]">{p.scope}</td>
                  <td className="py-4 pr-4">
                    <div className="font-display text-xl mv-green-text">
                      {p.jobs_low.toLocaleString()} – {p.jobs_high.toLocaleString()}{p.key === "phase-3" ? "+" : ""}
                    </div>
                    <div className="text-[11px]" style={{ color: "var(--mv-muted)" }}>jobs</div>
                  </td>
                  <td className="py-4 text-[13.5px]" style={{ color: "var(--mv-ink)" }}>{p.who}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-[12px] font-accent italic" style={{ color: "var(--mv-muted)" }}>
          Estimates based on internal build plans — actual outcomes will be published quarterly.
        </p>
      </section>

      {/* FAIR & PRIORITISED */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-14" data-testid="impact-fair-jobs">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Fair &amp; prioritised</span></div>
        <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3">Every job, on fair ground.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
          {FAIR_JOBS.map((f, i) => (
            <div key={i} className="mv-card p-5 flex items-start gap-3">
              <div className="mv-green-text mt-0.5">{f.icon}</div>
              <div className="text-[14px]" style={{ color: "var(--mv-ink)" }}>{f.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT MAKES THESE JOBS DIFFERENT */}
      <section className="border-t mv-hairline py-14" style={{ background: "linear-gradient(180deg, rgba(10,58,36,0.03), rgba(212,175,55,0.05))" }}>
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Fair Jobs Pledge</span></div>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3">These are not just temporary jobs.</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="impact-fair-jobs-different">
            {FAIR_JOBS_DIFFERENT.map((line, i) => (
              <div key={i} className="mv-card p-5 flex items-start gap-3">
                <BadgeCheck size={18} className="mt-0.5" style={{ color: "#0A7A2C" }}/>
                <div className="text-[14px]" style={{ color: "var(--mv-ink)" }}>{line}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INVESTOR IMPACT NUMBERS */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Investor impact</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          Every <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>£10,000</span> invested creates ~3–5 sustainable local jobs.
        </h2>
        <p className="mt-3 text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
          Every home built supports <strong>7+ local livelihoods</strong> for years to come.
        </p>
      </section>

      {/* IMPACT CALCULATOR */}
      <ImpactCalculator />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><RiskWarning compact /></div>

      {/* WIDER COMMUNITY BENEFITS */}
      <section className="border-t mv-hairline py-14" data-testid="impact-community">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Beyond jobs — community benefits</span></div>
          <h2 className="font-display text-3xl md:text-4xl mv-green-text mt-3">Homes grow neighbourhoods.</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
            {COMMUNITY_BENEFITS.map((b, i) => (
              <div key={i} className="mv-card p-5 flex items-start gap-3">
                <div className="mv-green-text mt-0.5">{b.icon}</div>
                <div className="text-[14px]" style={{ color: "var(--mv-ink)" }}>{b.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUARTERLY REAL IMPACT (public reports) */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-14" data-testid="impact-quarterly">
        <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>§ Quarterly real impact</span></div>
        <h2 className="font-display text-3xl md:text-5xl mv-green-text mt-3 leading-[1.05]">
          Real numbers. <span className="font-accent italic" style={{ color: "var(--mv-gold)" }}>Every quarter.</span>
        </h2>
        <p className="mt-3 max-w-2xl text-[14.5px]" style={{ color: "var(--mv-muted)" }}>
          Jobs created · Youth &amp; women hired · Training certificates issued · Local suppliers paid. No estimates — real names, real numbers, real lives changed.
        </p>

        {/* Totals to date */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { i: <Home size={20}/>, k: "Homes delivered",       v: totals.homes.toLocaleString(),    tid: "impact-total-homes" },
            { i: <Users size={20}/>, k: "Families helped",       v: totals.families.toLocaleString(), tid: "impact-total-families" },
            { i: <Wallet size={20}/>, k: "Fees saved for members", v: money(totals.fees),               tid: "impact-total-fees" },
          ].map((c) => (
            <div key={c.k} className="mv-card p-6" data-testid={c.tid}>
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center mb-3 mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>{c.i}</div>
              <div className="text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--mv-muted)" }}>{c.k}</div>
              <div className="font-display text-3xl mv-green-text mt-1">{c.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Public reports</span></div>
          {loading ? (
            <div className="mt-4 text-neutral-500">Loading…</div>
          ) : reports.length === 0 ? (
            <div className="mt-4 mv-card p-6 text-[14.5px]" style={{ color: "var(--mv-muted)" }} data-testid="impact-reports-empty">
              First public impact report will be published at the end of this quarter.
              We publish jobs created, youth/women hired, training certificates issued and local suppliers paid — no estimates, real names.
            </div>
          ) : (
            <div className="mt-4 space-y-4" data-testid="impact-reports-list">
              {reports.map((r) => (
                <div key={r.id} className="mv-card p-6">
                  <div className="flex flex-wrap justify-between items-baseline gap-3">
                    <div className="font-display text-xl mv-green-text">{r.month}</div>
                    <div className="text-[12px] tracking-[0.2em] uppercase" style={{ color: "var(--mv-muted)" }}>Public report</div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Homes</div>
                      <div className="font-display text-xl mv-green-text">{r.homes_delivered}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Families</div>
                      <div className="font-display text-xl mv-green-text">{r.families_helped}</div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Fees Saved</div>
                      <div className="font-display text-xl mv-green-text">{money(r.fees_saved, r.currency)}</div>
                    </div>
                  </div>
                  {r.summary && <p className="mt-4 text-[14px]" style={{ color: "var(--mv-text)" }}>{r.summary}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CLOSING QUOTE */}
      <section className="border-t mv-hairline py-16" style={{ background: "linear-gradient(180deg, #fff, rgba(212,175,55,0.10))" }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center" data-testid="impact-closing-quote">
          <Quote size={28} className="mx-auto" style={{ color: "var(--mv-gold)" }}/>
          <p className="mt-4 font-accent italic text-2xl md:text-3xl mv-green-text leading-[1.25]">
            "Others build homes for people. <span style={{ color: "var(--mv-gold)" }}>We build people for their homes</span> — and their communities."
          </p>
        </div>
      </section>
    </main>
  );
}
